
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from "../ui/button";
import { ArrowRight, Clock } from 'lucide-react';

export function LifetimeDealCTA() {
  const [remainingSpots, setRemainingSpots] = useState(100);
  const [timeLeft, setTimeLeft] = useState({
    days: 180, // ~6 months
    hours: 23,
    minutes: 59,
    seconds: 59
  });

  // Simulate countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <section className="py-20 bg-gradient-to-br from-white to-indigo-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute top-0 left-0 w-full h-64 bg-indigo-600 transform -skew-y-6 -translate-y-24 z-0"></div>
        <div className="absolute bottom-0 right-0 w-full h-64 bg-indigo-600 transform skew-y-6 translate-y-24 z-0"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              🚀 Lock in Your Pediatric Sleep Coach Lifetime Deal
              <span className="block mt-2 text-indigo-600">Limited Spots Available!</span>
            </h2>
            
            <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
              Don't miss this one-time opportunity to lock in SleepNest at our lowest price ever for pediatric sleep specialists!
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-xl p-8 sm:p-10 mb-12 border border-indigo-100"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="flex flex-col items-start p-4 bg-indigo-50 rounded-xl">
                <div className="bg-indigo-100 p-3 rounded-full mb-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Lowest Price – Forever</h3>
                <p className="text-gray-700">Get SleepNest for your pediatric sleep consulting practice at our introductory price and never worry about price increases—your rate is locked in for life!</p>
              </div>
              
              <div className="flex flex-col items-start p-4 bg-indigo-50 rounded-xl">
                <div className="bg-indigo-100 p-3 rounded-full mb-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Priority Access to Features</h3>
                <p className="text-gray-700">Pediatric sleep coach early adopters receive VIP status with priority access to new features and AI improvements before anyone else.</p>
              </div>
              
              <div className="flex flex-col items-start p-4 bg-indigo-50 rounded-xl">
                <div className="bg-indigo-100 p-3 rounded-full mb-4">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Exclusive Support</h3>
                <p className="text-gray-700">Pediatric sleep coach lifetime deal members get dedicated support and direct input into our product roadmap.</p>
              </div>
            </div>
            
            {/* Countdown and spots remaining */}
            <div className="my-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="bg-indigo-900 text-white rounded-xl p-6 order-2 md:order-1">
                <h4 className="font-semibold mb-4 flex items-center text-lg">
                  <Clock className="mr-2 h-5 w-5" /> Time Remaining (6 Months):
                </h4>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="bg-indigo-800 rounded-lg p-2">
                    <div className="text-2xl font-bold">{timeLeft.days}</div>
                    <div className="text-xs text-indigo-200">Days</div>
                  </div>
                  <div className="bg-indigo-800 rounded-lg p-2">
                    <div className="text-2xl font-bold">{timeLeft.hours}</div>
                    <div className="text-xs text-indigo-200">Hours</div>
                  </div>
                  <div className="bg-indigo-800 rounded-lg p-2">
                    <div className="text-2xl font-bold">{timeLeft.minutes}</div>
                    <div className="text-xs text-indigo-200">Mins</div>
                  </div>
                  <div className="bg-indigo-800 rounded-lg p-2">
                    <div className="text-2xl font-bold">{timeLeft.seconds}</div>
                    <div className="text-xs text-indigo-200">Secs</div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col items-center justify-center order-1 md:order-2">
                <div className="text-3xl font-bold text-indigo-900 mb-2">Only {remainingSpots} spots left!</div>
                <div className="w-full bg-gray-200 rounded-full h-4 mb-2 max-w-md">
                  <div className="bg-green-500 h-4 rounded-full" style={{ width: '70%' }}></div>
                </div>
                <p className="text-gray-600 text-sm">⏳ Before pricing increases permanently for pediatric sleep coaches</p>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="flex flex-col items-center mt-8">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="relative group"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-green-500 rounded-full opacity-75 blur-sm group-hover:opacity-100 transition duration-200"></div>
                <Button 
                  size="lg" 
                  className="relative bg-green-500 hover:bg-green-600 text-white font-bold rounded-full px-10 py-7 h-auto text-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-3"
                  asChild
                >
                  <Link to="/specialist-registration">
                    Claim Your Pediatric Sleep Coach Lifetime Deal <ArrowRight className="h-6 w-6 animate-pulse" />
                  </Link>
                </Button>
              </motion.div>
              
              <p className="mt-4 text-gray-700">
                No credit card required. 14-day money-back guarantee for pediatric sleep coaches.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}